import React, { useState } from "react";
import { Loader } from "lucide-react";
import ToastMessage from "../reuseable/ToastMessage";
import useNotificationsApi from "../../hooks/useNotificationsApi.js";

const NotificationSubscriberRegistration = ({ onSubscribersChange }) => {
  const { subscribeApi } = useNotificationsApi();

  const [formData, setFormData] = useState({ email: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      showToast("Please enter an email address", "error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    try {
      setIsLoading(true);
      await subscribeApi(formData.email);

      showToast(`✅ Successfully subscribed ${formData.email}`, "success");
      setFormData({ email: "" });
      if (onSubscribersChange) {
        onSubscribersChange();
      }
    } catch (error) {
      console.error("Subscription error", error);
      const errMsg = error.response?.data?.error || "Failed to subscribe. Please try again.";
      showToast(errMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px 0", maxWidth: "600px" }}>
      <h3
        style={{
          marginBottom: "24px",
          fontSize: "18px",
          fontWeight: "600",
          color: "#333",
        }}
      >
        📧 Add New Subscriber
      </h3>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "20px" }}>
          <label
            htmlFor="email"
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#333",
              fontSize: "14px",
            }}
          >
            Email Address
          </label>
          <input
            id="email"
            type="email"
            name="email"
            placeholder="subscriber@example.com"
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "12px 14px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              backgroundColor: isLoading ? "#f0f0f0" : "white",
              cursor: isLoading ? "not-allowed" : "auto",
              fontSize: "14px",
              boxSizing: "border-box",
              transition: "all 0.3s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#bb1ccc";
              e.target.style.boxShadow = "0 0 0 3px rgba(187,28,204,0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#ddd";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            background: isLoading
              ? "#ccc"
              : "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "6px",
            cursor: isLoading ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.3s ease",
            opacity: isLoading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(187,28,204,0.3)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          {isLoading ? (
            <>
              <Loader className="animate-spin" width={16} />
              Subscribing...
            </>
          ) : (
            <>+ Subscribe</>
          )}
        </button>
      </form>

      <ToastMessage toast={toast} />
    </div>
  );
};

export default NotificationSubscriberRegistration;